import * as fabric from 'fabric';

import { fabricObject, fabricWithId } from '../models/fabric.model';

export const FABRIC_CUSTOM_PROPERTIES = ['id', 'label', 'pieceWidth', 'pieceHeight'] as const;
type MeasureUnit = 'mm' | 'cm' | 'dm' | 'm';

const POINTS_PER_INCH = 72;
const MILLIMETERS_PER_INCH = 25.4;
const VECTORIAL_UNITS_PER_MILLIMETER = POINTS_PER_INCH / MILLIMETERS_PER_INCH;
const UNIT_SCALE_BY_MILLIMETER: Record<MeasureUnit, number> = {
  mm: 1,
  cm: 10,
  dm: 100,
  m: 1000,
};
const DEFAULT_ROTATION_CURSOR_POSITION = '7.25 10';
const ROTATION_CONTROL_ICON_SIZE = 32;
const IMAGE_LOAD_TIMEOUT_MS = 5000;

let rotateIcon: HTMLImageElement | null = null;

const imageElementCache = new Map<string, Promise<HTMLImageElement>>();

const rotationCursorPositions = new Map<number, string>([
  [-90, '9.25 5.25'],
  [-75, '9.972 3.863'],
  [-60, '10.84 1.756'],
  [-45, '11.972 -1.716'],
  [-30, '18.83 0.17'],
  [-15, '28.49 -9.49'],
  [15, '-7.985 46.77'],
  [30, '-0.415 27.57'],
  [45, '2.32 21.713'],
  [60, '3.916 18.243'],
  [75, '4.762 16.135'],
  [90, '5.25 14.75'],
  [105, '5.84 13.617'],
  [120, '6.084 12.666'],
  [135, '6.317 12.01'],
  [150, '6.754 11.325'],
  [165, '7.06 10.653'],
  [180, '7.25 10'],
  [195, '7.597 9.43'],
  [210, '7.825 8.672'],
  [225, '7.974 7.99'],
  [240, '8.383 7.332'],
  [255, '8.83 6.441'],
]);

function assertValidImageSource(sourceUrl: string): void {
  if (sourceUrl.trim().length === 0) {
    throw new Error('Fabric image source URL is required');
  }
}

function assertValidImageDimension(value: number, dimension: 'width' | 'height'): void {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`Fabric image ${dimension} must be a positive finite number`);
  }
}

/**
 * Resolves a detached, Fabric-owned image element for a source URL.
 *
 * Elements are never taken from the live DOM: Fabric keeps a reference to whatever element it is
 * given and re-reads it on every render, so an Angular-bound `<img>` would repaint canvas pieces
 * with another piece's picture as soon as its `[src]` is rebound. Repeat loads are deduplicated by
 * absolute URL and served from the browser cache instead.
 */
function loadImageElement(src: string): Promise<HTMLImageElement> {
  assertValidImageSource(src);

  const cacheKey = toAbsoluteResourceUrl(src) ?? src;
  const cachedImageLoad = imageElementCache.get(cacheKey);
  if (cachedImageLoad) {
    return cachedImageLoad;
  }

  const imageLoad = loadNewImageElement(src).catch((error: unknown): never => {
    imageElementCache.delete(cacheKey);

    throw error;
  });
  imageElementCache.set(cacheKey, imageLoad);

  return imageLoad;
}

function loadNewImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject): void => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    const timeoutId = globalThis.setTimeout((): void => {
      image.onload = null;
      image.onerror = null;
      reject(new Error(`Timed out loading image: ${src}`));
    }, IMAGE_LOAD_TIMEOUT_MS);

    image.onload = (): void => {
      globalThis.clearTimeout(timeoutId);
      resolve(image);
    };
    image.onerror = (): void => {
      globalThis.clearTimeout(timeoutId);
      reject(new Error(`Failed to load image: ${src}`));
    };
    image.src = src;
  });
}

function toAbsoluteResourceUrl(url: string): string | undefined {
  try {
    return new URL(url, globalThis.location?.href).href;
  } catch {
    return undefined;
  }
}

export async function createFabricImageForPiece(sourceUrl: string, width: number, height: number): Promise<fabric.FabricImage> {
  assertValidImageSource(sourceUrl);
  assertValidImageDimension(width, 'width');
  assertValidImageDimension(height, 'height');

  const imageElement = await loadImageElement(sourceUrl);
  const sourceWidth = imageElement.naturalWidth || imageElement.width;
  const sourceHeight = imageElement.naturalHeight || imageElement.height;
  assertValidImageDimension(sourceWidth, 'width');
  assertValidImageDimension(sourceHeight, 'height');

  const fabricImage = new fabric.FabricImage(imageElement, {
    width: sourceWidth,
    height: sourceHeight,
    objectCaching: false,
  });
  fabricImage.getSrc = (): string => sourceUrl;
  fabricImage.set({
    scaleX: width / sourceWidth,
    scaleY: height / sourceHeight,
  });
  fabricImage.setCoords();

  return fabricImage;
}

export function isRuler(object: fabricWithId | fabricObject | fabric.FabricObject): boolean {
  const objectWithMetadata = object as { label?: string };

  return objectWithMetadata.label === 'indicator' || isTextObject(object);
}

export function vectorialToLinearMeasure(value: number, unit: MeasureUnit = 'cm'): number {
  return value / VECTORIAL_UNITS_PER_MILLIMETER / UNIT_SCALE_BY_MILLIMETER[unit];
}

export function linearMeasureToVectorial(value: number, unit: MeasureUnit = 'cm'): number {
  return value * UNIT_SCALE_BY_MILLIMETER[unit] * VECTORIAL_UNITS_PER_MILLIMETER;
}

export function renderRotationControlIcon(
  ctx: CanvasRenderingContext2D,
  left: number,
  top: number,
  _styleOverride: unknown,
  fabricObject: fabric.FabricObject,
): void {
  const icon = getRotateIcon();
  if (!icon) {
    return;
  }

  ctx.save();
  ctx.translate(left, top);
  ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle ?? 0));
  ctx.drawImage(icon, -ROTATION_CONTROL_ICON_SIZE / 2, -ROTATION_CONTROL_ICON_SIZE / 2, ROTATION_CONTROL_ICON_SIZE, ROTATION_CONTROL_ICON_SIZE);
  ctx.restore();
}

export function getRotationCursor(angle: number): string {
  const relativeAngle = angle - 90;
  const transform =
    relativeAngle === 0 ? 'translate(9.5 3.5)' : `rotate(${relativeAngle} ${rotationCursorPositions.get(relativeAngle) ?? DEFAULT_ROTATION_CURSOR_POSITION})`;

  const imgCursor = encodeURIComponent(`
    <svg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='24' height='24'>
      <defs>
        <filter id='a' width='266.7%' height='156.2%' x='-75%' y='-21.9%' filterUnits='objectBoundingBox'>
          <feOffset dy='1' in='SourceAlpha' result='shadowOffsetOuter1'/>
          <feGaussianBlur in='shadowOffsetOuter1' result='shadowBlurOuter1' stdDeviation='1'/>
          <feColorMatrix in='shadowBlurOuter1' result='shadowMatrixOuter1' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.2 0'/>
          <feMerge>
            <feMergeNode in='shadowMatrixOuter1'/>
            <feMergeNode in='SourceGraphic'/>
          </feMerge>
        </filter>
        <path id='b' d='M1.67 12.67a7.7 7.7 0 0 0 0-9.34L0 5V0h5L3.24 1.76a9.9 9.9 0 0 1 0 12.48L5 16H0v-5l1.67 1.67z'/>
      </defs>
      <g fill='none' fill-rule='evenodd'><path d='M0 24V0h24v24z'/>
        <g fill-rule='nonzero' filter='url(#a)' transform='${transform}'>
          <use fill='#000' fill-rule='evenodd' xlink:href='#b'/>
          <path stroke='#FFF' d='M1.6 11.9a7.21 7.21 0 0 0 0-7.8L-.5 6.2V-.5h6.7L3.9 1.8a10.4 10.4 0 0 1 0 12.4l2.3 2.3H-.5V9.8l2.1 2.1z'/>
        </g>
      </g>
    </svg>`);

  return `url("data:image/svg+xml;charset=utf-8,${imgCursor}") 12 12, crosshair`;
}

export function snapRotationAngle(angle: number): number {
  return angle - (angle % 15);
}

function isTextObject(object: fabricWithId | fabricObject | fabric.FabricObject): boolean {
  if ('isType' in object) {
    return object.isType('Text', 'text');
  }

  return object.type === 'Text' || object.type === 'text';
}

function getRotateIcon(): HTMLImageElement | null {
  const document = globalThis.document;
  if (!document) {
    return null;
  }

  if (!rotateIcon) {
    rotateIcon = document.createElement('img');
    rotateIcon.src = 'assets/images/rotate.svg';
  }

  return rotateIcon;
}
